import type { SupabaseClient } from '@supabase/supabase-js';

import type { Database } from '@/integrations/supabase/types';

/**
 * Durable image-generation completion for `concept_renditions`, as pure
 * functions of an INJECTED client.
 *
 * 🔴 **This module must never import `BaseService`, the browser Supabase
 * client, or anything that does.** `server/api/generate-image.post.ts` imports
 * it, and `src/integrations/supabase/client.ts` builds a `createClient` at
 * module load, which throws "Node.js 20 detected without native WebSocket
 * support" on the repo's pinned Node 20 runtime — taking down every
 * `/api/generate-image` request, not only concept ones. Unit tests hide this
 * because they mock Supabase. It is the same split `ArtifactProductLinkService`
 * and `brand-kit-server` use: type-only imports, client passed in.
 * `ConceptRenditionService` re-exports both functions for browser callers.
 */

/**
 * THE SERVER-SIDE COMPLETION ATTACH.
 *
 * `useWorkbenchPrompt.submit` stamps a fresh `generation_token` on the
 * rendition BEFORE the request goes out (via `setGenerating`). When the
 * provider finishes, this function — called from inside
 * `server/api/generate-image.post.ts` itself, not only from the client's
 * post-fetch promise chain — writes the media back onto the row that asked
 * for it. A client that reloads, HMRs, or crashes mid-request no longer
 * strands the rendition in "Generating..." forever.
 *
 * The row is identified by `renditionId` alone (a primary key); `conceptId`
 * and `ownerId` scope it so a caller can never touch another account's row.
 * The aspect ratio is deliberately NOT a filter — it adds nothing to a
 * primary key and only gave the match a way to miss.
 *
 * Matched by `generation_token`, not merely `is_generating`: a token match
 * proves THIS specific in-flight request still owns the row. A SECOND
 * generation for the same rendition (a retry, or a resubmit before the first
 * call returns) mints a new token, so the FIRST request's late completion
 * no-ops instead of overwriting a fresher image.
 *
 * `ownerId` must already be the RESOLVED account owner — the caller verifies
 * that. Idempotent: the same token and media id read as "already done".
 */
export async function attachGeneratedMediaByCorrelation(
  client: SupabaseClient<Database>,
  input: {
    conceptId: string;
    ownerId: string;
    renditionId: string;
    generationToken: string;
    mediaId: string;
  },
): Promise<{ attached: boolean; reason?: string }> {
  const { data, error } = await client
    .from('concept_renditions')
    .select('id, version, generation_token, media_id, follows_set_image')
    .eq('id', input.renditionId)
    .eq('project_task_id', input.conceptId)
    .eq('user_id', input.ownerId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return { attached: false, reason: 'rendition_not_found' };

  const row = data as {
    id: string;
    version: number;
    generation_token: string | null;
    media_id: string | null;
    follows_set_image: boolean;
  };
  if (row.media_id === input.mediaId && !row.follows_set_image) {
    return { attached: true, reason: 'already_attached' };
  }
  if (row.generation_token !== input.generationToken) {
    return { attached: false, reason: 'generation_token_mismatch' };
  }

  const { data: updated, error: updateError } = await client
    .from('concept_renditions')
    .update({
      media_id: input.mediaId,
      follows_set_image: false,
      is_generating: false,
      generation_token: null,
      version: row.version + 1,
    })
    .eq('id', row.id)
    .eq('version', row.version)
    .select('id')
    .maybeSingle();
  if (updateError) throw new Error(updateError.message);
  // A miss here means a concurrent write already moved the version — the
  // client's own `attachMedia` CAS (with its rebase-and-retry) still lands
  // the media id on its next attempt, so this is a benign race, never a lost
  // update.
  return { attached: Boolean(updated), reason: updated ? undefined : 'version_race' };
}

/**
 * THE ORPHAN RECONCILER. A rendition whose `is_generating` never turned back
 * off — the server crashed before replying, the network dropped the
 * response — reads as "Generating..." on every future load. Anything
 * `is_generating` older than `timeoutMs` is stale by definition: no real
 * provider call runs anywhere near that long
 * (`CONCEPT_RENDITION_GENERATION_STALE_TIMEOUT_MS`). This flips it to an
 * HONEST failed state — `is_generating: false`, image left exactly as it
 * was. It returns the ids it cleared so the caller can tell the person the
 * last attempt did not finish instead of pretending nothing was tried.
 *
 * A row with no `generation_started_at` (stamped before the correlation
 * columns existed, or by a caller that never sent a token) is aged by its
 * `updated_at` instead — otherwise exactly the rows already stuck in prod
 * would be the ones this could never clear.
 *
 * Deliberately does NOT bump `version`: it only clears a stuck flag, never a
 * user-authored field, so it cannot collide with a real edit's CAS.
 */
export async function reconcileStaleGenerations(
  client: SupabaseClient<Database>,
  input: { conceptId: string; ownerId: string; timeoutMs: number },
): Promise<string[]> {
  const staleBefore = new Date(Date.now() - input.timeoutMs).toISOString();
  const staleFilter = `generation_started_at.lt.${staleBefore},and(generation_started_at.is.null,updated_at.lt.${staleBefore})`;
  const { data: candidates, error: selectError } = await client
    .from('concept_renditions')
    .select('id')
    .eq('project_task_id', input.conceptId)
    .eq('user_id', input.ownerId)
    .eq('is_generating', true)
    .or(staleFilter);
  if (selectError) throw new Error(selectError.message);
  const staleIds = (candidates ?? []).map((row) => (row as { id: string }).id);
  if (staleIds.length === 0) return [];
  const { data, error } = await client
    .from('concept_renditions')
    .update({ is_generating: false, generation_token: null })
    .in('id', staleIds)
    .eq('project_task_id', input.conceptId)
    .eq('user_id', input.ownerId)
    .eq('is_generating', true)
    .or(staleFilter)
    .select('id');
  if (error) throw new Error(error.message);
  return (data ?? []).map((row) => (row as { id: string }).id);
}
